<script>
   /*********************************************************************/
   /* Contributions modal                                               */
   /* --------------------                                              */
   /* plot variables contributions to q/h distances for selected object */
   /*********************************************************************/

   import { Multilines, Rectangles, Points, Lines, TextLabels } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';
   import { range } from 'mdatools/stat';
   import { scale as prep_scale } from 'mdatools/prep';
   import { csvRow, downloadCSV, escapeText } from '@mdatools-apps/shared/utils';

   import Axes from '@mdatools-apps/shared/plots/Axes.svelte';


   let {
      props,
      params,
      selected,
   } = $props();

   const datasets = $derived(props && props.data ? props.data : null);
   const dataset = $derived(datasets && params.resID ? datasets[params.resID] : null);


   // index and x-coordinate of the selected variable
   let sel = $state(-1);


   // mouse handle for selection of variables
   function mouseHandler(e) {
      // refuse a lone rect: the axes box and the highlight each sit alone in their group
      if (e.target.tagName !== 'rect' || e.target.parentNode.children.length < 2) {
         sel = -1;
         return;
      }

      const id = Array.from(e.target.parentNode.children).indexOf(e.target);
      if (id < 0) {
         sel = -1;
         return;
      }

      // if index is the same as the highlighted variable, do nothing
      if (sel === id) return;
      sel = id;
   }

   // set x-axis values and limits
   const xc = $derived.by(() => {
      const values = vector(dataset.varvalues);
      const n = values.length;                               // number of variables
      const dil = (values.v[1] - values.v[0]) / 2;           // half distance between two first x-variables
      const dir = (values.v[n - 1] - values.v[n - 2]) / 2;   // half distance between two last x-variables
      const rng = [values.v[0], values.v[n - 1]];            // range of x-variables (assuming they are ordered)
      const lim = [rng[0] - dil, rng[1] + dir];
      const width = Math.abs(rng[1] - rng[0]) / n;
      return {values, lim, width, hwidth: width * 0.5}
   });

   // compute coordinates of rectangles for selection if necessary
   const left = $derived(xc ? (
         dataset.varrev ?
         xc.values.apply(v => v + xc.hwidth) :
         xc.values.apply(v => v - xc.hwidth)
      ) : null);

   // compute explained and residual parts of data and the full data
   const X = $derived(dataset && props && props.model ?  prep_scale(dataset.values, props.model.mX, props.model.sX) : null);
   const Xt = $derived(X ? X.t() : null);

   let xp = $derived(Xt ? Xt.getcolumn(selected >= 0 ? selected + 1 : 1) : null);

   let yc = $derived.by(() => {
      const vc = {all: Xt, sel: xp, label: 'Preprocessed data, X'};

      const rng = range(vc.all.v);
      const height = rng[1] - rng[0];
      const lim = [rng[0] - height/50, rng[1] + height/12];
      const top = Vector.fill(rng[1], vc.sel.length);
      vc['lim'] = lim;
      vc['top'] = top;
      vc['height'] = height;
      return vc;
   });

   // y-coordinate of selected point and label
   const hx = $derived(xc && sel >= 0 ? xc.values.v[sel] : null);
   const hy = $derived(yc && sel >= 0 ? yc.sel.v[sel] : null);
   const hl = $derived(dataset && sel >= 0 ? dataset.varlabels[sel] : null);

   // set plot title
   const title = $derived(dataset && selected >= 0 ? `Object ${dataset.objlabels[selected].replace("_", "-")}` : null);
   const xLabel = $derived(dataset ? dataset.varvaluesName + (dataset.varvaluesUnits ? ', ' + dataset.varvaluesUnits : '') : null);

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {

      const { sep, dec } = dataset;
      let out = csvRow(['Labels', ...dataset.varlabels], sep, dec);
      for (let i = 0; i < Xt.ncols; i++) {
         out += csvRow([dataset.objlabels[i], ...Xt.getcolref(i + 1)], sep, dec);
      }

      downloadCSV(out, 'plot-dataset');
   }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
{#if xc && yc}
   <!-- svelte-ignore a11y_click_events_have_key_events -->
   <div class="plot-container" role="tooltip" onclick={mouseHandler}>
      <Axes {title} yLabel={yc.label} {xLabel} limY={yc.lim} limX={xc.lim} {downloadCSVCallback} fileName='plot-dataset' >

      <!-- all outcomes -->
      <Multilines xValues={xc.values} yValues={yc.all} lineColor={selected >= 0 ? '#e0e0e0' : '#a0a0a0'}/>

      <!-- outcomes for the selected objects -->
      {#if selected >= 0}
      <Lines xValues={xc.values} yValues={yc.sel} lineColor="crimson"  />
      {/if}

      <!-- rectangles to make selection possible -->
      <Rectangles {left} top={yc.top} height={yc.height} width={xc.width} faceColor='transparent' borderColor='transparent'/>

      {#if sel >= 0}
         <!-- highlighted variable -->
         <TextLabels xValues={[hx]} yValues={[yc.top.v[0]]} labels={[escapeText(hl)]} pos={3}
            textSize={0.85}/>
         <Points xValues={[hx]} yValues={[hy]} faceColor='crimson' lineColor='crimson'/>
         <Rectangles left={[left.v[sel]]} top={[yc.top.v[sel]]} height={yc.height} width={xc.width} faceColor='#ff000015'/>
      {/if}
   </Axes>
   </div>
{/if}

<style>
   .plot-container {
      width: 100%;
      height: 100%;
   }
</style>
